import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Eye, EyeOff, MousePointerClick } from "lucide-react";

const field = "w-full bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none";
const REG = ["Regulated", "In Progress", "Minimal"];

const emptyForm = {
  code: "", name: "", flag: "", iso3: "", lat: "", lng: "",
  adoption_rate: "", rank: "", readiness: "", yoy_change: "",
  regulation_status: "In Progress", regulation_name: "",
  has_strategy: true, strategy_name: "", gov_link: "",
  top_tools: "", insight: "", match_names: "", published: true,
};

export default function CountryIntelAdmin() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null); // null | "new" | doc
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/countries").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setModal("new"); };
  const openEdit = (d) => {
    const clean = {};
    Object.keys(emptyForm).forEach((k) => { clean[k] = d[k] == null ? emptyForm[k] : d[k]; });
    setForm({
      ...clean,
      top_tools: (d.top_tools || []).join(", "),
      match_names: (d.match_names || []).join(", "),
    });
    setModal(d);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      lat: form.lat === "" ? null : Number(form.lat),
      lng: form.lng === "" ? null : Number(form.lng),
      adoption_rate: form.adoption_rate === "" ? null : Number(form.adoption_rate),
      rank: form.rank === "" ? null : Number(form.rank),
      readiness: form.readiness === "" ? null : Number(form.readiness),
      top_tools: form.top_tools.split(",").map((s) => s.trim()).filter(Boolean),
      match_names: form.match_names.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (modal === "new") await api.post("/admin/countries", payload);
      else await api.patch(`/admin/countries/${modal.id}`, payload);
      toast.success("Country saved");
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const togglePublish = async (d) => {
    await api.patch(`/admin/countries/${d.id}`, { published: !d.published }).catch(() => {});
    load();
  };

  const del = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    await api.delete(`/admin/countries/${d.id}`).catch(() => {});
    load();
  };

  const mostClicked = [...rows].sort((a, b) => (b.click_count || 0) - (a.click_count || 0)).slice(0, 5);

  return (
    <div data-testid="country-intel-admin">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <p className="font-mono-tech text-xs text-[#94A3B8]">{rows.length} countries · {rows.filter((r) => r.published).length} published</p>
        <button onClick={openNew} data-testid="country-add-btn"
          className="flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110 text-sm">
          <Plus className="w-4 h-4" /> Add country
        </button>
      </div>

      {mostClicked.some((c) => c.click_count) && (
        <div className="card-surface rounded-xl p-4 mb-5" data-testid="country-most-clicked">
          <div className="font-mono-tech text-xs uppercase text-[#94A3B8] mb-2 flex items-center gap-1.5"><MousePointerClick className="w-3.5 h-3.5" /> Most clicked</div>
          <div className="flex flex-wrap gap-2">
            {mostClicked.filter((c) => c.click_count).map((c) => (
              <span key={c.id} className="text-xs px-3 py-1 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30">
                {c.flag} {c.name} · {c.click_count}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {rows.map((d) => (
          <div key={d.id} className="card-surface rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`country-row-${d.code}`}>
            <div className="min-w-0">
              <div className="font-display font-bold flex items-center gap-2">
                <span className="text-lg">{d.flag}</span> {d.name}
                <span className="text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[#94A3B8]">{d.code}</span>
                {!d.published && <span className="text-[10px] font-mono-tech uppercase px-2 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#fbbf24] border border-[#F59E0B]/30">Draft</span>}
              </div>
              <p className="text-sm text-[#94A3B8] mt-0.5">
                Rank #{d.rank} · Adoption {d.adoption_rate}% · Readiness {d.readiness} · {d.click_count || 0} clicks
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => togglePublish(d)} data-testid={`country-publish-${d.code}`}
                className="text-xs flex items-center gap-1 px-2.5 py-1.5 rounded border border-white/15 hover:border-[#00F0FF]/40 text-[#94A3B8]">
                {d.published ? <><Eye className="w-3.5 h-3.5" /> Published</> : <><EyeOff className="w-3.5 h-3.5" /> Draft</>}
              </button>
              <button onClick={() => openEdit(d)} data-testid={`country-edit-${d.code}`}
                className="text-[#00F0FF] hover:bg-[#00F0FF]/10 p-2 rounded"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(d)} data-testid={`country-delete-${d.code}`}
                className="text-[#EF4444] hover:bg-[#EF4444]/10 p-2 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
          <div className="card-surface rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-xl">{modal === "new" ? "Add Country" : `Edit ${modal.name}`}</h3>
              <button onClick={() => setModal(null)} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <input value={form.code} onChange={(e) => set("code", e.target.value)} required placeholder="Code (US)" className={field} data-testid="country-form-code" />
                <input value={form.iso3} onChange={(e) => set("iso3", e.target.value)} placeholder="ISO3 (USA)" className={field} />
                <input value={form.flag} onChange={(e) => set("flag", e.target.value)} placeholder="Flag 🇺🇸" className={field} />
                <input value={form.rank} onChange={(e) => set("rank", e.target.value)} type="number" placeholder="Rank" className={field} />
              </div>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} required placeholder="Country name" className={field} data-testid="country-form-name" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <input value={form.lat} onChange={(e) => set("lat", e.target.value)} type="number" step="any" placeholder="Lat" className={field} />
                <input value={form.lng} onChange={(e) => set("lng", e.target.value)} type="number" step="any" placeholder="Lng" className={field} />
                <input value={form.adoption_rate} onChange={(e) => set("adoption_rate", e.target.value)} type="number" step="any" placeholder="Adoption %" className={field} data-testid="country-form-adoption" />
                <input value={form.readiness} onChange={(e) => set("readiness", e.target.value)} type="number" step="any" placeholder="Readiness" className={field} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.yoy_change} onChange={(e) => set("yoy_change", e.target.value)} placeholder="YoY change (+8.2%)" className={field} />
                <select value={form.regulation_status} onChange={(e) => set("regulation_status", e.target.value)} className={field}>
                  {REG.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <input value={form.regulation_name} onChange={(e) => set("regulation_name", e.target.value)} placeholder="Regulation name" className={field} />
              <div className="grid grid-cols-[auto,1fr] gap-3 items-center">
                <label className="flex items-center gap-2 text-sm text-[#94A3B8] whitespace-nowrap"><input type="checkbox" checked={form.has_strategy} onChange={(e) => set("has_strategy", e.target.checked)} /> Has strategy</label>
                <input value={form.strategy_name} onChange={(e) => set("strategy_name", e.target.value)} placeholder="Strategy name" className={field} />
              </div>
              <input value={form.gov_link} onChange={(e) => set("gov_link", e.target.value)} placeholder="Government link (https://)" className={field} />
              <input value={form.top_tools} onChange={(e) => set("top_tools", e.target.value)} placeholder="Top tools, comma separated" className={field} />
              <input value={form.match_names} onChange={(e) => set("match_names", e.target.value)} placeholder="Job/event match names, comma separated (e.g. United States, USA)" className={field} />
              <textarea value={form.insight} onChange={(e) => set("insight", e.target.value)} rows={3} placeholder="Key AI insight" className={field} data-testid="country-form-insight" />
              <label className="flex items-center gap-2 text-sm text-[#94A3B8]"><input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} data-testid="country-form-published" /> Published (visible on globe)</label>
              <button disabled={busy} className="w-full bg-[#00F0FF] text-[#04121a] font-semibold py-2.5 rounded-md" data-testid="country-form-submit">
                {busy ? "Saving…" : "Save country"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
